import React from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  Link,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";

import { supabase } from "../../components/SupabaseClient";
import AuthShell from "./components/AuthShell.jsx";
import AuthBrandPanel from "./components/AuthBrandPanel.jsx";
import AuthFormHeader from "./components/AuthFormHeader.jsx";
import AuthField from "./components/AuthField.jsx";
import {
  AUTH_BRAND_CONTENT,
  AUTH_FORM_HEADERS,
} from "./components/authContents";
import { primaryBtnSx } from "./components/authStyles";
import {
  checkUserExists,
  isValidEmail,
} from "../../utils/userCredentialValidation";

const RESET_REDIRECT_URL = `${window.location.origin}/reset-password`;
const COUNTDOWN_SECONDS = 30;

function ForgotPasswordPage() {
  const [email, setEmail] = React.useState("");
  const [emailError, setEmailError] = React.useState("");
  const [submitError, setSubmitError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [countdown, setCountdown] = React.useState(0);

  React.useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const canSubmit =
    email.trim() !== "" && isValidEmail(email) && !loading && countdown === 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    const lowerEmail = email.trim().toLowerCase();

    if (!isValidEmail(lowerEmail)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError("");
    setLoading(true);

    const { exists, error: userLookupError } =
      await checkUserExists(lowerEmail);
    if (userLookupError) {
      setLoading(false);
      setSubmitError(
        "Unable to process this request right now. Please try again.",
      );
      return;
    }

    if (!exists) {
      setLoading(false);
      setOpenDialog(true);
      setCountdown(COUNTDOWN_SECONDS);
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(lowerEmail, {
        redirectTo: RESET_REDIRECT_URL,
      });

      if (error) {
        console.error("resetPasswordForEmail error:", error);
        setSubmitError(error.message || "Failed to send reset email.");
        setLoading(false);
        return;
      }

      setOpenDialog(true);
      setCountdown(COUNTDOWN_SECONDS);
    } catch (err) {
      console.error("reset password caught err:", err);
      setSubmitError(err.message || "Failed to send reset email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        bgcolor: "#EEF1F0",
      }}
    >
      <AuthShell
        brand={<AuthBrandPanel {...AUTH_BRAND_CONTENT.forgotPassword} />}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <AuthFormHeader {...AUTH_FORM_HEADERS.forgotPassword} />

          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          <AuthField
            label="Email"
            name="email"
            type="email"
            autoComplete="email"
            autoFocus
            placeholder="Enter your email"
            value={email}
            onChange={(e) => {
              const v = e.target.value;
              setEmail(v);
              if (v.trim() === "" || isValidEmail(v)) {
                setEmailError("");
              } else {
                setEmailError("Please enter a valid email address.");
              }
            }}
            error={emailError}
            startIcon={<MailOutlineIcon fontSize="small" />}
          />

          <Button
            type="submit"
            fullWidth
            disabled={!canSubmit}
            sx={primaryBtnSx}
            startIcon={
              loading ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : null
            }
          >
            {countdown > 0
              ? `Resend reset link (${countdown}s)`
              : "Send reset link"}
          </Button>

          <Typography align="center" variant="body2" sx={{ mt: 3 }}>
            Remembered it?{" "}
            <Link
              component={RouterLink}
              to="/signin"
              underline="hover"
              sx={{ fontWeight: 600 }}
            >
              Sign In
            </Link>
          </Typography>
        </Box>
      </AuthShell>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxWidth: 460,
            width: "100%",
            p: 1,
            boxShadow: "0 20px 50px rgba(16,24,40,0.18)",
          },
        }}
      >
        <DialogContent sx={{ textAlign: "center", px: 4, pt: 4, pb: 1.5 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              mx: "auto",
              mb: 2.5,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(31,78,85,0.10)",
              color: "#1F4E55",
            }}
          >
            <MarkEmailReadOutlinedIcon sx={{ fontSize: 32 }} />
          </Box>

          <Typography
            sx={{
              fontSize: "1.25rem",
              fontWeight: 700,
              color: "#0f172a",
              mb: 1,
            }}
          >
            Check your inbox
          </Typography>
          <Typography
            sx={{
              fontSize: "0.95rem",
              color: "text.secondary",
              lineHeight: 1.6,
              textAlign: "left",
            }}
          >
            If an EFabric account exists for{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "#0f172a" }}>
              {email.trim().toLowerCase()}
            </Box>
            , a password reset link will arrive shortly.
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: "text.secondary",
              mt: 1.5,
              textAlign: "left",
            }}
          >
            Didn&apos;t get it? Check your spam folder
            {countdown > 0 ? ` — you can resend in ${countdown}s` : ""}.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 3, pt: 1 }}>
          <Button
            fullWidth
            onClick={() => setOpenDialog(false)}
            sx={{ ...primaryBtnSx, mt: 0 }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ForgotPasswordPage;
